import { useState, useEffect } from 'react';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { getPlatform, isNative, isAndroid, isIOS, Platform } from '../lib/platform';

interface PlatformInfo {
  platform: Platform;
  isNative: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isWeb: boolean;
  safeAreaInsets: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

/**
 * Hook for accessing platform-specific information and utilities
 */
export function usePlatform(): PlatformInfo {
  const [safeAreaInsets, setSafeAreaInsets] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  useEffect(() => {
    // Get safe area insets from CSS environment variables
    const computeInsets = () => {
      const style = getComputedStyle(document.documentElement);
      setSafeAreaInsets({
        top: parseInt(style.getPropertyValue('--sat') || '0', 10) || 0,
        bottom: parseInt(style.getPropertyValue('--sab') || '0', 10) || 0,
        left: parseInt(style.getPropertyValue('--sal') || '0', 10) || 0,
        right: parseInt(style.getPropertyValue('--sar') || '0', 10) || 0,
      });
    };

    computeInsets();
    window.addEventListener('resize', computeInsets);
    return () => window.removeEventListener('resize', computeInsets);
  }, []);

  const platform = getPlatform();

  return {
    platform,
    isNative: isNative(),
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    isWeb: platform === 'web',
    safeAreaInsets,
  };
}

/**
 * Initialize native platform features
 * Call this once when the app starts
 */
export async function initializePlatform(): Promise<void> {
  if (!isNative()) {
    return;
  }

  try {
    // Configure status bar - default to light style (dark text) for light mode
    // This will be updated by ThemeContext when theme is determined
    if (isIOS() || isAndroid()) {
      await StatusBar.setStyle({ style: Style.Light });
      
      if (isAndroid()) {
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
      }
    }

    // Hide splash screen after app is ready
    await SplashScreen.hide();
  } catch (error) {
    console.warn('Error initializing platform:', error);
  }
}

/**
 * Update status bar style based on theme
 * @param isDarkMode - whether the app is in dark mode
 */
export async function setStatusBarStyle(isDarkMode: boolean): Promise<void> {
  if (!isNative()) {
    return;
  }

  try {
    if (isIOS() || isAndroid()) {
      // Style.Light = dark text (for light backgrounds)
      // Style.Dark = light text (for dark backgrounds)
      await StatusBar.setStyle({ 
        style: isDarkMode ? Style.Dark : Style.Light 
      });
      
      if (isAndroid()) {
        await StatusBar.setBackgroundColor({ 
          color: isDarkMode ? '#1a1a2e' : '#ffffff' 
        });
      }
    }
  } catch (error) {
    console.warn('Error setting status bar style:', error);
  }
}

/**
 * Hook for handling Android back button
 */
export function useBackButton(handler?: () => boolean): void {
  useEffect(() => {
    if (!isAndroid()) return;

    const backButtonListener = App.addListener('backButton', ({ canGoBack }) => {
      // If handler returns true, it handled the back action
      if (handler && handler()) {
        return;
      }

      // Default behavior: go back in history or exit app
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    return () => {
      backButtonListener.then((listener) => listener.remove());
    };
  }, [handler]);
}

/**
 * Hook for handling app state changes (foreground/background)
 */
export function useAppState(
  onForeground?: () => void,
  onBackground?: () => void
): void {
  useEffect(() => {
    if (!isNative()) return;

    const stateListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && onForeground) {
        onForeground();
      } else if (!isActive && onBackground) {
        onBackground();
      }
    });

    return () => {
      stateListener.then((listener) => listener.remove());
    };
  }, [onForeground, onBackground]);
}


